// Library Methods
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ExamCardHeader from "src/components/containers/shared/ExamSliderList/ExamSliderCard/exam-cards/card-ui/ExamCardHeader/ExamCardHeader";
import VideoCardContainer from "./card-ui/VideoCardContainer/VideoCardContainer";

// Utils
import { getYouTubeEmbedUrl } from "src/utils/text/textHelper";

// Constants
import { examCardEnums } from "src/constants/enums";

// Contexts
import { useExamSliderContext } from "src/contexts/exams/ExamSliderContext";

type VideoSliderCardType = {
  label: string;
  url: string;
  groupId: number;
};

const YOUTUBE_REGEX =
  /(?:youtube\.com\/(?:watch\?v=|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/;

const VideoSliderCard = ({ label, url, groupId }: VideoSliderCardType) => {
  const { t } = useTranslation();
  const { actions } = useExamSliderContext();
  const { onRemoveCard } = actions;

  const [hasError, setHasError] = useState(false);

  const onRemoveVideo = useCallback(() => {
    onRemoveCard(examCardEnums.VIDEO.algorithmPath, groupId);
  }, [onRemoveCard, groupId]);

  return (
    <VideoCardContainer>
      <ExamCardHeader label={label} onRemoveCard={onRemoveVideo} />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          minHeight: 0,
        }}
      >
        {YOUTUBE_REGEX.test(url) ? (
          <iframe
            src={getYouTubeEmbedUrl(url)}
            title={label}
            allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            referrerPolicy="strict-origin-when-cross-origin"
            allowFullScreen
            style={{
              borderRadius: "8px",
              border: "none",
              width: "100%",
              aspectRatio: "16 / 9",
              minHeight: "200px",
            }}
          />
        ) : (
          <>
            {!hasError && (
              <video
                src={url}
                controls
                style={{
                  objectFit: "cover",
                  borderRadius: "8px",
                  width: "100%",
                  display: "block",
                }}
                onError={() => setHasError(true)}
              />
            )}
            {hasError && (
              <Box
                sx={{
                  backgroundColor: "#000",
                  borderRadius: "8px",
                  width: "100%",
                  flexGrow: 1,
                  minHeight: "200px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <Typography sx={{ color: "#FFF", p: "20px", fontSize: "14px" }}>
                  {t("Video could not be loaded. Please check the URL.")}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </VideoCardContainer>
  );
};

export default VideoSliderCard;
